import React from 'react';
import { array, string, object } from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import { lazyLoadWithDimensions } from '../../util/contextHelpers';
import { ensureListing, getUpcomingExceptions, userDisplayNameAsString } from '../../util/data';
import { getDefaultTimeZoneOnBrowser } from '../../util/dates';
import { richText } from '../../util/richText';
import { findOptionsForSelectFilter } from '../../util/search';
import { createSlug } from '../../util/urlHelpers';
import { AspectRatioWrapper, NamedLink, ResponsiveImage, Icon } from '..';
import { AVAILABILITY_EXCEPTION_METADATA_ACCESS_MEMBERS } from '../../util/types';
import config from '../../config';
import moment from 'moment-timezone';
import classNames from 'classnames';

import css from './LiveRoomCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const getThemeInfo = (themeOptions, key) => {
  return themeOptions.find(c => c.key === key);
};

const defaultTimeZone = () =>
  typeof window !== 'undefined' ? getDefaultTimeZoneOnBrowser() : 'Etc/UTC';

const formatExceptionDateTime = (timestamp, timeZone) => {
  return moment.tz(parseInt(timestamp, 10), timeZone).format('MMM D @h:mm A');
};

const ListingImage = props => <ResponsiveImage {...props} />;
const LazyImage = lazyLoadWithDimensions(ListingImage, { loadAfterInitialRendering: 3000 });

export const LiveRoomCardComponent = props => {
  const { className, rootClassName, listing, renderSizes, filtersConfig } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const currentAuthor = currentListing ? currentListing.author : null;
  const authorDisplayName = userDisplayNameAsString(currentAuthor);

  const id = currentListing.id.uuid;
  const { title = '', publicData, metadata } = currentListing.attributes;
  const slug = createSlug(title);
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const themeOptions = findOptionsForSelectFilter('theme', filtersConfig);
  const theme = publicData ? getThemeInfo(themeOptions, publicData.theme) : null;

  const show = publicData.show;
  const subscriptions = metadata?.subscriptions ? metadata.subscriptions.length : 0;

  const upcomingAvailabilityExceptions = getUpcomingExceptions(
    listing.attributes.publicData.exceptions,
    2
  );
  const hasUpcomingAvailabilityExceptions = upcomingAvailabilityExceptions.length > 0;

  return (
    <NamedLink className={classes} name="LiveRoomPage" params={{ id, slug }}>
      <AspectRatioWrapper width={3} height={2}>
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={['landscape-crop', 'landscape-crop2x']}
          sizes={renderSizes}
        />
      </AspectRatioWrapper>
      <div className={css.info}>
        <div className={css.title}>
          {richText(title, {
            longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
            longWordClass: css.longWord,
          })}
        </div>
        <div className={css.subscriptions}>
          <FormattedMessage
            id="LiveRoomCard.subscriptions"
            values={{
              subscriptions: <span className={css.subscriptionsCount}>{subscriptions}</span>,
              subscriptionLength: subscriptions,
            }}
          />
        </div>
        <div className={css.author}>
          <FormattedMessage
            id="LiveRoomCard.author"
            values={{
              displayName: <span className={css.authorName}>{authorDisplayName}</span>,
            }}
          />
        </div>
        <div className={css.details}>
          {theme ? (
            <span className={classNames(css.detailsItem, css.strong)}>{theme.label}</span>
          ) : null}
          {show ? (
            <span className={css.detailsItem}>
              <FormattedMessage
                id="LiveRoomCard.page"
                values={{ name: <strong className={css.strong}>{show.name}</strong> }}
              />
            </span>
          ) : null}
        </div>
        {hasUpcomingAvailabilityExceptions ? (
          <div className={css.availabilityExceptionsContainer}>
            <div className={css.availabilityExceptionsHeading}>
              <FormattedMessage id="LiveRoomCard.availabilityExceptionsHeading" />
            </div>
            <div className={css.availabilityExceptions}>
              {upcomingAvailabilityExceptions.map(e => {
                const isForMembers =
                  e.attributes.access === AVAILABILITY_EXCEPTION_METADATA_ACCESS_MEMBERS;
                return (
                  <span key={e.id} className={css.availabilityException}>
                    {isForMembers ? (
                      <Icon name="premium" size={12} className={css.availabilityExceptionIcon} />
                    ) : null}
                    {formatExceptionDateTime(e.attributes.start, defaultTimeZone())}
                  </span>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </NamedLink>
  );
};

LiveRoomCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  aspectWrapperClassName: null,
  renderSizes: null,
  filtersConfig: config.custom.filters,
};

LiveRoomCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  aspectWrapperClassName: string,
  filtersConfig: array,

  listing: object.isRequired,

  // Responsive image sizes hint
  renderSizes: string,
};

export default LiveRoomCardComponent;
